import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Button, IconButton, Avatar, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import InlineToggleCell from '../../components/InlineToggleCell';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const MinigamesPage = () => {
    const navigate = useNavigate();
    const [minigames, setMinigames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchMinigames = useCallback(() => {
        setLoading(true);
        apiService.get('/api/minigames')
            .then(data => setMinigames(data))
            .catch(err => setError(err.message || 'Failed to fetch minigames'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchMinigames();
    }, [fetchMinigames]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/minigames/${itemToDelete.id}`);
            fetchMinigames();
        } catch (err) {
            setError('Failed to delete minigame.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 50,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/minigames/${cell.getValue()}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'iconUrl',
            header: 'Icon',
            size: 60,
            Cell: ({cell, row}) => (
                <Avatar src={cell.getValue()} alt={row.original.name}/>
            ),
        },
        {
            accessorKey: 'identifier',
            header: 'Identifier',
            size: 120,
        },
        {
            accessorKey: 'name',
            header: 'Name',
            Cell: ({cell, row}) => (
                <Link component={RouterLink} to={`/minigames/${row.original.id}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'enabled',
            header: 'Enabled',
            size: 100,
            Cell: ({row}) => (
                <InlineToggleCell
                    protectedBy={MinigamesPage}
                    protectAction="update"
                    value={row.original.enabled}
                    save={(next) => apiService.put(
                        `/api/minigames/${row.original.id}/enabled`,
                        {value: next},
                    )}
                    onSaved={(updated) => setMinigames(prev =>
                        prev.map(m => m.id === updated.id ? updated : m)
                    )}
                    labels={{on: 'Enabled', off: 'Disabled'}}
                    tooltip="Click to toggle enabled"
                />
            ),
        },
        {
            accessorKey: 'visible',
            header: 'Visible',
            size: 100,
            Cell: ({row}) => (
                <InlineToggleCell
                    protectedBy={MinigamesPage}
                    protectAction="update"
                    value={row.original.visible}
                    save={(next) => apiService.put(
                        `/api/minigames/${row.original.id}/visible`,
                        {value: next},
                    )}
                    onSaved={(updated) => setMinigames(prev =>
                        prev.map(m => m.id === updated.id ? updated : m)
                    )}
                    labels={{on: 'Visible', off: 'Hidden'}}
                    colors={{on: 'info', off: 'default'}}
                    tooltip="Click to toggle visibility"
                />
            ),
        },
        {
            accessorKey: 'dailyPlayLimit',
            header: 'Daily Limit',
            size: 100,
            Cell: ({cell}) => cell.getValue() ?? '-',
        },
        {
            accessorKey: 'cooldownSeconds',
            header: 'Cooldown (s)',
            size: 100,
            Cell: ({cell}) => cell.getValue() ?? '-',
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: minigames,
        enablePagination: false,
        enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {showGlobalFilter: true},
        renderTopToolbarCustomActions: () => (
            <Protected component={MinigamesPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('/minigames/new')}>
                    Create Minigame
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/minigames/${row.original.id}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={MinigamesPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => {
                            setItemToDelete(row.original);
                            setConfirmOpen(true);
                        }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Minigames</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={MinigamesPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Minigame: ${itemToDelete?.name}?`}
                    message="Are you sure you want to delete this minigame? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

MinigamesPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.SUPPORT, ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default MinigamesPage;
